import { forwardRef, useMemo, useState, type ChangeEvent, type FocusEvent, useId } from 'react';

import { format, isValid, parse } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type Matcher } from 'react-day-picker';

// Import base CSS from react-day-picker v9 (safe to import more than once)
import 'react-day-picker/src/style.css';

import { TANKEF_DAYPICKER_CLASS, TANKEF_DAYPICKER_THEME_CSS } from '../../dayPickerTheme';
import { cn } from '../../utils';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../Popover';
import { maskDate } from './maskDate';

export interface DateInputProps {
  /** Current value, formatted `DD/MM/AAAA`. Empty string when unset. */
  value?: string;
  /** Called with the new value, formatted `DD/MM/AAAA` (may be partial while typing). */
  onChange?: (value: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Notched label floating over the border */
  label?: string;
  /** Error message (renders below, and recolors the border) */
  error?: string;
  /** Hint text (renders below when there is no error) */
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  id?: string;
  name?: string;
  /** Earliest selectable date in the calendar. Free typing isn't blocked by this. */
  minDate?: Date;
  /** Latest selectable date in the calendar. Free typing isn't blocked by this. */
  maxDate?: Date;
  locale?: 'es' | 'en';
}

const containerSize = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
};

const localeMap = { es, en: enUS };

function parseDisplayDate(value: string): Date | undefined {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return undefined;
  const parsed = parse(value, 'dd/MM/yyyy', new Date());
  return isValid(parsed) ? parsed : undefined;
}

/**
 * DateInput - Campo de fecha con máscara `DD/MM/AAAA` + selector de
 * calendario (popover). Reemplaza el uso de `<input type="date">` (formato
 * inconsistente entre navegadores/SO) y de inputs de texto libres sin máscara
 * ni forma de elegir la fecha visualmente.
 *
 * Controlado, igual que `PhoneInput`: `value`/`onChange` siempre transportan
 * el string formateado `DD/MM/AAAA` (puede ser parcial mientras se escribe).
 * Si el consumidor necesita ISO `YYYY-MM-DD` (contrato de BE), convierte en
 * el borde con `isoToDisplayDate`/`displayToIsoDate` de `@tankef/core`.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  {
    value = '',
    onChange,
    onBlur,
    label,
    error,
    hint,
    placeholder = 'DD/MM/AAAA',
    disabled = false,
    size = 'md',
    className,
    id,
    name,
    minDate,
    maxDate,
    locale = 'es',
  },
  ref
) {
  const reactId = useId();
  const inputId = id || `date-${reactId}`;
  const hasError = Boolean(error);
  const [isFocused, setIsFocused] = useState(false);
  const [open, setOpen] = useState(false);

  const selectedDate = useMemo(() => parseDisplayDate(value), [value]);

  const disabledMatchers = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers;
  }, [minDate, maxDate]);

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(maskDate(e.target.value));
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleDaySelect = (date: Date | undefined) => {
    if (!date) return;
    onChange?.(format(date, 'dd/MM/yyyy'));
    setOpen(false);
  };

  const borderColor = hasError
    ? 'border-tkf-line-error'
    : isFocused
      ? 'border-primary-500'
      : 'border-tkf-line-medium';

  const labelColor = hasError
    ? 'text-tkf-fg-error'
    : isFocused
      ? 'text-primary-500'
      : 'text-tkf-fg-muted';

  return (
    <div className={cn('w-full', className)}>
      <style>{TANKEF_DAYPICKER_THEME_CSS}</style>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            {label && (
              <label
                htmlFor={inputId}
                style={{
                  background: disabled
                    ? 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface-muted) 45%)'
                    : 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface) 45%)',
                }}
                className={cn(
                  'pointer-events-none absolute left-3 top-0 -translate-y-1/2',
                  'px-1 text-sm font-medium transition-colors duration-200',
                  labelColor,
                  disabled && 'text-tkf-fg-disabled'
                )}
              >
                {label}
              </label>
            )}

            <input
              ref={ref}
              id={inputId}
              name={name}
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              value={value}
              onChange={handleTextChange}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={10}
              aria-invalid={hasError}
              aria-describedby={
                hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
              }
              className={cn(
                'block w-full rounded-lg border bg-tkf-surface pl-3 pr-11 text-tkf-fg transition-colors duration-200',
                'focus:outline-none focus:ring-1',
                'disabled:cursor-not-allowed disabled:bg-tkf-surface-muted disabled:text-tkf-fg-disabled',
                'placeholder:text-tkf-fg-subtle',
                hasError
                  ? 'focus:border-tkf-line-error focus:ring-tkf-line-error'
                  : 'focus:border-primary-500 focus:ring-primary-500',
                borderColor,
                containerSize[size]
              )}
            />

            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Abrir calendario"
                className={cn(
                  'absolute inset-y-0 right-0 flex items-center pr-3',
                  'text-tkf-fg-subtle hover:text-primary-500',
                  'disabled:cursor-not-allowed disabled:hover:text-tkf-fg-subtle'
                )}
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>

        <PopoverContent align="end" sideOffset={8} className="p-3">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDaySelect}
            defaultMonth={selectedDate ?? maxDate ?? new Date()}
            captionLayout="dropdown"
            startMonth={minDate ?? new Date(1900, 0)}
            endMonth={maxDate ?? new Date(2100, 0)}
            disabled={disabledMatchers}
            locale={localeMap[locale]}
            showOutsideDays
            fixedWeeks
            className={TANKEF_DAYPICKER_CLASS}
            components={{
              Chevron: ({ orientation }) =>
                orientation === 'left' ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                ),
            }}
          />
        </PopoverContent>
      </Popover>

      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-tkf-fg-error" role="alert">
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
          {hint}
        </p>
      )}
    </div>
  );
});

DateInput.displayName = 'DateInput';
